import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { fetchData, postData } from '../../helpers/apiUtils';
import {
  DnvBlacklistStats,
  DnvBlacklistUploadResult,
  DnvBlacklistPurgeResult,
} from '../../helpers/dnvAssetAnalysisData';

const formatInt = (n: number): string => n.toLocaleString('de-DE');

const DnvBlacklist: FC = () => {
  const [stats, setStats] = useState<DnvBlacklistStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<DnvBlacklistUploadResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<DnvBlacklistPurgeResult | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  const loadStats = async () => {
    setStatsLoading(true);
    setStatsError(null);
    const result = await fetchData<DnvBlacklistStats>('/api/dnv-asset-analysis/blacklist/stats');
    if (result.success && result.data) {
      setStats(result.data);
    } else {
      setStatsError(result.error || 'Fehler beim Laden der Statistik');
    }
    setStatsLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchData<DnvBlacklistStats>('/api/dnv-asset-analysis/blacklist/stats');
      if (cancelled) return;
      if (result.success && result.data) setStats(result.data);
      else setStatsError(result.error || 'Fehler beim Laden der Statistik');
      setStatsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setUploadResult(null);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadResult(null);
    setUploadError(null);
    try {
      const csv = await selectedFile.text();
      const result = await postData<DnvBlacklistUploadResult>(
        '/api/dnv-asset-analysis/blacklist/upload',
        { csv },
      );
      if (result.success && result.data) {
        setUploadResult(result.data);
        await loadStats();
      } else {
        setUploadError(result.error || 'Upload fehlgeschlagen');
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Datei konnte nicht gelesen werden');
    }
    setUploading(false);
  };

  const handlePurge = async () => {
    if (
      !window.confirm(
        'Mailchimp-Purge starten?\n\nAlle Emails aus dnv_blacklist werden DAUERHAFT aus der DNV-Mailchimp-Liste entfernt. Idempotent, aber bei ~600k Emails dauert die Übertragung mehrere Minuten.',
      )
    ) {
      return;
    }
    setPurging(true);
    setPurgeResult(null);
    setPurgeError(null);
    const result = await postData<DnvBlacklistPurgeResult>(
      '/api/dnv-asset-analysis/blacklist/purge',
      {},
    );
    if (result.success && result.data) {
      setPurgeResult(result.data);
    } else {
      setPurgeError(result.error || 'Purge fehlgeschlagen');
    }
    setPurging(false);
  };

  return (
    <Box>
      <Typography variant="h6" mb={1}>
        DNV Blacklist
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        CSV-Upload von Email-Adressen in die <code>dnv_blacklist</code>-Tabelle. Der Mailchimp-Purge
        löscht alle Listen-Mitglieder mit Adressen aus der Blacklist <strong>dauerhaft</strong> von
        der DNV-Liste (<code>list_id 3ea50d9913</code>) via Batch-API.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Aktueller Bestand
          </Typography>
          {statsLoading ? (
            <CircularProgress size={20} />
          ) : statsError ? (
            <Alert severity="error">{statsError}</Alert>
          ) : stats ? (
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Einträge
                </Typography>
                <Typography variant="h5">{formatInt(stats.total)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Frühester Eintrag
                </Typography>
                <Typography variant="body1">{stats.earliest_added_at || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Letzter Eintrag
                </Typography>
                <Typography variant="body1">{stats.latest_added_at || '—'}</Typography>
              </Box>
            </Stack>
          ) : null}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            CSV hochladen
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Eine Email pro Zeile, erste Spalte. Doppelte Einträge und ungültige Adressen werden
            übersprungen. Bestehende Adressen (PK-Kollision) bleiben unverändert.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
            >
              Datei wählen
              <input type="file" accept=".csv,text/csv,text/plain" hidden onChange={handleFileChange} />
            </Button>
            <Typography variant="body2">
              {selectedFile ? selectedFile.name : 'Keine Datei ausgewählt'}
            </Typography>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? <CircularProgress size={20} /> : 'Upload starten'}
            </Button>
          </Stack>
          {uploadError && <Alert severity="error">{uploadError}</Alert>}
          {uploadResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {formatInt(uploadResult.parsed)} Zeilen verarbeitet,{' '}
                {formatInt(uploadResult.valid)} gültige Emails übernommen,{' '}
                {formatInt(uploadResult.invalid)} ungültig.
              </Typography>
              {uploadResult.invalid_samples.length > 0 && (
                <Box mt={1}>
                  <Typography variant="caption">
                    Erste {uploadResult.invalid_samples.length} ungültige Adressen:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {uploadResult.invalid_samples.map((e, i) => (
                      <li key={i}>
                        <code>{e}</code>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Mailchimp-Purge
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Sendet <code>delete-permanent</code> Batch-Operations für alle Blacklist-Adressen an
            die DNV-Mailchimp-Liste. Idempotent (nicht-existente Member werden ignoriert).
            Mailchimp verarbeitet die Batches asynchron — die Endpoint-Antwort enthält nur die
            <code> batch_ids</code>. Läuft zusätzlich täglich um 03:00 als Cron.
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={handlePurge}
            disabled={purging}
          >
            {purging ? <CircularProgress size={20} /> : 'Purge jetzt starten (Admin)'}
          </Button>
          {purgeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {purgeError}
            </Alert>
          )}
          {purgeResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {formatInt(purgeResult.emails_submitted)} Emails in{' '}
                {formatInt(purgeResult.batches)} Batches an Mailchimp übergeben.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                Start: {purgeResult.started_at} · Ende: {purgeResult.completed_at}
              </Typography>
              {purgeResult.batch_ids.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
                  Erste batch_ids: {purgeResult.batch_ids.slice(0, 5).join(', ')}
                  {purgeResult.batch_ids.length > 5 && ' …'}
                </Typography>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DnvBlacklist;
